import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { ReactElement, useEffect } from 'react';
import { AppLinkFallbackType } from '../../../applink/AppLinkFallbackType.enum';
import { pagewebUrlMatcher } from '../../../applink/PagewebUrlMatcher.singleton';
import { TrackPageViewContainer } from '../../../common/containers/TrackPageViewContainer';
import { getEnv } from '../../../common/env/env';
import { getDehydratedState } from '../../../common/getDehydrateState';
import { redirectInInitialProps } from '../../../common/redirectInInitialProps';
import { requestContext } from '../../../common/requestContext';
import useTypedDispatch from '../../../common/useTypedDispatch';
import useTypedSelector from '../../../common/useTypedSelector';
import { Layout } from '../../../design-system/layout/Layout';
import { FooterType } from '../../../design-system/layout/LayoutFooter/FooterType.enum';
import { AppDownload } from '../../../design-system/organisms/AppDownload/AppDownload';
import { ContentMain } from '../../../design-system/organisms/ContentMain/ContentMain';
import { ContentAgeAuthLimitCheckerHeadless } from '../../../domain/common/headless/ContentAgeAuthLimitCheckerHeadless';
import { CONTENT_HOME_TAB_LOGGING_TITLE } from '../../../domain/content/[seriesId]/constant';
import { ContentMainMobileContainer } from '../../../domain/content/[seriesId]/container/ContentMainMobileContainer';
import { ContentMainPcContainer } from '../../../domain/content/[seriesId]/container/ContentMainPcContainer';
import { ContentOnlyLoggedInOrSaleCheckerHeadless } from '../../../domain/content/[seriesId]/headless/ContentOnlyLoggedInOrSaleCheckerHeadless';
import { useContentMainTicketActionInfo } from '../../../domain/content/[seriesId]/hook/useContentMainTicketActionInfo';
import { useSelectContentHomeTabType } from '../../../domain/content/[seriesId]/hook/useSelectContentHomeTabType';
import { setCanRead, setNeedAutoScroll, setNeedFetchOpenPreview } from '../../../domain/content/[seriesId]/state/contentHomeSlice';
import { ContentHomeTabType } from '../../../domain/content/[seriesId]/type';
import { setCurrentPage } from '../../../domain/content/[seriesId]/viewer/[productId]/state/imageViewerSlice';
import { clearCurrentProgress } from '../../../domain/content/[seriesId]/viewer/[productId]/state/textViewerSlice';
import { ApiErrorBoundary } from '../../../errors/ApiErrorBoundary.class';
import { getSerializedPagewebError } from '../../../errors/getSerializedPagewebError.function';
import { ItemNotSaleError } from '../../../errors/ItemNotSaleError';
import { PagewebErrorType } from '../../../errors/PagewebErrorType.enum';
import { contentFactory } from '../../../factory/contentFactory';
import { setFooter } from '../../../footer/FooterSlice';
import { SaleMethod, SaleState, SlideType } from '../../../graphql/generated/types';
import { ExceptionMobileHeaderType } from '../../../header/constants';
import logger from '../../../log/logger';
import { BM_TO_BM_type_MAP } from '../../../logging/constants';
import { MetaInfo } from '../../../meta/MetaInfo.interface';
import { CACHE_ONLY_CONFIG, ONLY_DATA_CONFIG } from '../../../reactQuery/constant';
import { createQueryClient } from '../../../reactQuery/createQueryClient';
import { ReactQueryFetcher } from '../../../reactQuery/ReactQueryFetcher';
import { nextReduxWrapper } from '../../../state/store';
import { useContentHomeOverviewQuery } from '../../../wrapper';
function ContentMainPage({
  seriesId
}: {
  seriesId: number;
}) {
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const {
    data
  } = useContentHomeOverviewQuery({
    seriesId
  }, ONLY_DATA_CONFIG);
  const {
    category,
    title,
    bm,
    subcategory
  } = data?.contentHomeOverview?.content || {};
  const preview = data?.contentHomeOverview?.content?.serviceProperty?.preview;

  // preview 탭을 보여줄 수 있는 상태에서 query로 명확하게 탭이 선택되지 않았다면 preview 탭으로 보내줍니다.
  const tabType = useSelectContentHomeTabType(seriesId);
  const {
    ticketActionInfo
  } = useContentMainTicketActionInfo({
    seriesId,
    isInPreviewTab: tabType === ContentHomeTabType.preview
  });
  const dispatch = useTypedDispatch();
  const router = useRouter();
  useEffect(() => {
    if (isMobile && tabType === ContentHomeTabType.preview) {
      dispatch(setFooter({
        mobile: {
          isExposed: false
        }
      }));
    }
  }, [dispatch, isMobile, tabType]);
  const tabName = CONTENT_HOME_TAB_LOGGING_TITLE[tabType];
  const isEnteredContentHome = useTypedSelector(state => state.contentHome.isEnteredContentHome[seriesId]);
  useEffect(() => {
    function handleRouteChange(url: string) {
      if (!preview?.item) {
        return;
      }
      const REGEX_CONTENT_HOME = new RegExp(`\\/content\\/${seriesId}($|\\?)`, 'g');
      if (!REGEX_CONTENT_HOME.test(url) && !url.startsWith(`/content/${seriesId}/viewer/${preview.item.productId}`)) {
        const {
          productId,
          slideType
        } = preview.item;

        // 컨텐츠홈 외부로 이동할 때 첫 화 보기를 위한 데이터를 제거합니다.
        if (slideType === SlideType.Epub) {
          dispatch(clearCurrentProgress({
            productId
          }));
        } else {
          dispatch(setCurrentPage({
            productId,
            page: 1
          }));
        }
      }
    }
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [dispatch, preview, router, seriesId]);
  useEffect(() => {
    return () => {
      dispatch(setNeedAutoScroll({
        needAutoScroll: true
      }));
      dispatch(setNeedFetchOpenPreview({
        needFetchOpenPreview: true
      }));
    };
  }, [dispatch, seriesId]);
  return <>
      <ContentMain className="flex h-full flex-1 flex-col">
        <ApiErrorBoundary>
          <ReactQueryFetcher reactQuery={useContentHomeOverviewQuery} ignoreFetching={true} variables={{
          seriesId
        }} onSuccess={({
          contentHomeOverview
        }) => {
          if (contentHomeOverview?.content?.saleState === SaleState.Ing) {
            dispatch(setCanRead({
              seriesId,
              canRead: true
            }));
          }
        }} options={{
          ...CACHE_ONLY_CONFIG
        }}>
            <ContentAgeAuthLimitCheckerHeadless seriesId={seriesId} location="contenthome">
              {/* 19세는 ContentAgeAuthLimitChecker 내에서 PV 로깅 진행 */}
              {category && title && bm && <TrackPageViewContainer key="콘텐츠홈" trackObj={{
              section: '콘텐츠홈',
              page: `콘텐츠홈_${tabName}`,
              actionType: 'Pageview',
              actionName: `콘텐츠홈_${tabName}_화면`,
              page_meta: {
                id: String(seriesId),
                type: 'seriesId',
                category,
                subcategory,
                series: title,
                series_id: String(seriesId)
              },
              customProps: {
                page_readtype: ticketActionInfo?.copy,
                user_action: 'pv',
                BM_type: BM_TO_BM_type_MAP[bm]
              }
            }} />}
              <ContentOnlyLoggedInOrSaleCheckerHeadless seriesId={seriesId} ifStop={<ItemNotSaleError isMobile={isMobile} onClickGoToHome={() => {
              window.location.assign('/');
            }} />}>
                {isMobile && <ContentMainMobileContainer seriesId={seriesId} tabType={tabType} isWebView={isWebView} />}
                {!isMobile && <ContentMainPcContainer seriesId={seriesId} tabType={tabType} />}
              </ContentOnlyLoggedInOrSaleCheckerHeadless>
            </ContentAgeAuthLimitCheckerHeadless>
          </ReactQueryFetcher>
        </ApiErrorBoundary>
      </ContentMain>
      {/* TODO : 선택 구매에 대한 상태가 추가 되면 여기에 상태를 추가하여 앱다운로드를 미노출 시켜야 합니다. */}
      {!isMobile && isEnteredContentHome && <AppDownload isMobile={false} isFullBannerOnPc={true} onClickAppLink={() => window.location.assign(pagewebUrlMatcher.getCurrentAppLink({
      schemePrefixType: 'applink',
      fallbackType: AppLinkFallbackType.Store
    }))} />}
    </>;
}
ContentMainPage.getInitialProps = nextReduxWrapper.getInitialPageProps(store => async context => {
  const seriesId = Number(context.query?.seriesId);
  let metaInfo: Maybe<MetaInfo> = undefined;
  const queryClient = createQueryClient();
  const cacheKey = useContentHomeOverviewQuery.getKey({
    seriesId
  });
  queryClient.invalidateQueries({
    queryKey: cacheKey
  });
  try {
    const data = await queryClient.fetchQuery(contentFactory.contentHomeOverview({
      seriesId
    }, {
      ...CACHE_ONLY_CONFIG
    }, {
      ssrRequest: context.req
    }));
    if (data.contentHomeOverview?.content?.saleState === SaleState.Ing) {
      store.dispatch(setCanRead({
        seriesId,
        canRead: true
      }));
    }
    const {
      title,
      thumbnail,
      landThumbnail,
      authors,
      category,
      description,
      translateProperty,
      saleMethod
    } = data?.contentHomeOverview?.content || {};
    if (![SaleMethod.Cash, SaleMethod.Ticket].includes(saleMethod as SaleMethod)) {
      throw new Error('유효하지 않은 타입입니다.');
    }
    metaInfo = data?.contentHomeOverview?.content ? {
      title: `${title} - ${translateProperty?.category?.[data.contentHomeOverview.content.lang] || category}`,
      ogTitle: title,
      image: landThumbnail || thumbnail,
      keywords: `${title}, ${authors}`,
      description: description ? description.replace(/\n/g, '') : '',
      url: `${getEnv().webOrigin}/content/${seriesId}`,
      author: authors,
      lang: data.contentHomeOverview.content.lang
    } : undefined;
  } catch (e) {
    queryClient.resetQueries({
      queryKey: cacheKey
    });
    const serializedError = getSerializedPagewebError(e);

    //  잘못된 input 등일 때 apolloError 가 발생. 이 경우 404 로 띄워준다
    if (serializedError.type === PagewebErrorType.ApolloError) {
      redirectInInitialProps({
        destination: '/404',
        res: context.res
      });
      return {};
    }

    // 연령을 충족하지 못했더라도 overview api 는 성공해야한다.
    logger.err('콘텐츠홈 Generation 실패', e);
  }
  return {
    seriesId,
    metaInfo,
    ...getDehydratedState(queryClient)
  };
});
ContentMainPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={page.props.metaInfo?.title || '콘텐츠홈'} metaInfo={page.props.metaInfo} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true,
    headerType: ExceptionMobileHeaderType.ContentHome,
    footerType: FooterType.ContentHome
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true,
    footerType: FooterType.ContentHome
  }}>
      {page}
    </Layout>;
};
export default ContentMainPage;